import { defineStore } from "pinia";
import api from "@/modules/api";
import { useMate } from "@/stores/mate";
import { Admirer } from "@/types/entity";
import { useScopedMateId } from "@/composable/useScopedMateId";

const PAGE_SIZE = 999;

interface AdmireState {
  reloadIdx: number;
  admiring: {
    list: Admirer[];
    pageNum: number;
    changed: boolean;
  };
  admirer: {
    list: Admirer[];
    pageNum: number;
    changed: boolean;
  };
}

export const useAdmire = defineStore("admire", {
  state: (): AdmireState => {
    return {
      reloadIdx: 0,
      admiring: {
        list: [],
        pageNum: 1,
        changed: false,
      },
      admirer: {
        list: [],
        pageNum: 1,
        changed: false,
      },
    };
  },
  actions: {
    async getAdmiringList(): Promise<Admirer[]> {
      if (
        this.admiring.list &&
        !this.admiring.changed &&
        this.admiring.list.length > 0
      ) {
        return this.admiring.list;
      } else {
        return await this.fetchAdmiringList();
      }
    },
    async getAdmirerList(): Promise<Admirer[]> {
      if (
        this.admirer.list &&
        !this.admirer.changed &&
        this.admirer.list.length > 0
      ) {
        return this.admirer.list;
      } else {
        return await this.fetchAdmirerList();
      }
    },
    async fetchAdmiringList(): Promise<Admirer[]> {
      const mate = useMate();
      const res = await api.readAdmirerPage({
        filter: { fanId: mate.userMate!.id },
        pagination: { page: 1, perPage: PAGE_SIZE },
        sort: { field: "id", order: "desc" },
      });
      this.admiring.list = res.page.data;
      this.admiring.changed = false;
      return this.admiring.list;
    },
    async fetchAdmirerList(): Promise<Admirer[]> {
      const mate = useMate();
      const res = await api.readAdmirerPage({
        filter: { mateId: mate.currentMate!.id },
        pagination: { page: 1, perPage: PAGE_SIZE },
        sort: { field: "id", order: "desc" },
      });
      this.admirer.list = res.page.data;
      this.admirer.changed = false;
      return this.admirer.list;
    },
    async admire(mateId: number): Promise<Admirer> {
      const res = await api.createOneAdmirerRequest(
        useMate().userMate!.id!,
        mateId
      );
      return res.item;
    },
    /**
     * 取消守护
     * @param recordId 要取消的记录的PK
     */
    async cancelAdmire(recordId: number): Promise<void> {
      const userCancelAdmire = useScopedMateId(async () => {
        await api.deleteOneAdmirer(recordId);
        this.admiring.list = this.admiring.list.filter(
          (item) => item.id !== recordId
        );
        this.admirer.list = this.admirer.list.filter(
          (item) => item.id !== recordId
        );
      });
      return await userCancelAdmire();
    },
    /**
     * @param mateId 要判断的Mate的ID
     * @returns 如果关注了，返回Follower的PK；如果没有关注，返回null
     */
    async isAdmiring(mateId: number): Promise<Admirer | null> {
      const isUserAdmire = useScopedMateId(async () => {
        let admirer: Admirer | null = null;
        const admirerList = await this.fetchAdmiringList();
        admirer = admirerList.find((item) => item.mateId === mateId) ?? null;
        if (admirer) return admirer;

        const mate = useMate();
        const res = await api.readAdmirerRequestPage({
          filter: {
            receiverId: mateId,
            mateId: mate.userMate!.id,
            status: "pending",
            role: "sender",
          },
        });
        if (res.page.data.length > 0) admirer = res.page.data[0] as any;
        return admirer;
      });
      return await isUserAdmire();
    },
    /**
     * @param mateId 要判断的Mate的ID
     * @returns 如果关注了，返回Follower的PK；如果没有关注，返回null
     */
    async isSuperAdmiring(mateId: number): Promise<Admirer | null> {
      const mate = useMate();
      let admirer: Admirer | null = null;
      const res = await api.readAdmireSubscriptionPage({
        filter: {
          recipientId: mateId,
          admirerId: mate.currentMate!.id,
          type: 2,
          status: 2,
        },
      });
      if (res.page.data.length > 0) admirer = res.page.data[0] as any;
      return admirer;
    },
    disableCache(cleanData = false): void {
      this.admiring.changed = true;
      this.admirer.changed = true;
      if (cleanData) {
        this.admiring.list = [];
        this.admirer.list = [];
      }
    },
  },
});
